import { IMapService } from "../types/mapService";
import {
  isReadyMessage,
  isSaveAndExitResponse,
  isEditingStartedMessage,
  isSetBaseLineResponse,
} from "../bintactMapMessages";
import { bintactMapUrl } from "src/config";

/**
 * Default map service implementation for Agro-Informatics map.
 * The URL is sourced from REACT_APP_BINTACT_MAP_URL; callers MUST provide a
 * non-empty value (the constructor throws otherwise).
 */
export class AgroInformaticsMapService implements IMapService {
  private readonly mapUrl: string;
  private readonly targetOrigin: string;

  constructor(mapUrl: string = bintactMapUrl) {
    if (!mapUrl) {
      throw new Error(
        "BINTACT map URL is not configured: set REACT_APP_BINTACT_MAP_URL.",
      );
    }
    this.mapUrl = mapUrl;
    this.targetOrigin = new URL(mapUrl).origin;
  }

  getMapUrl(): string {
    return this.mapUrl;
  }

  getTargetOrigin(): string {
    return this.targetOrigin;
  }

  sendMessage(iframe: HTMLIFrameElement, message: unknown): void {
    if (!iframe.contentWindow) {
      console.error("Cannot send message: iframe contentWindow is not available");
      return;
    }

    try {
      iframe.contentWindow.postMessage(message, this.targetOrigin);
    } catch (error) {
      console.error("Failed to send message to map service:", error);
      throw error;
    }
  }

  validateMessageOrigin(origin: string): boolean {
    return origin === this.targetOrigin;
  }

  isMessageFromService(data: unknown): boolean {
    // Use type guard functions to validate incoming messages from the service
    // These functions already validate the message structure and source
    
    // 1. Check for BINTACT_AGRO_INFORMATICS_SOURCE string messages
    if (isReadyMessage(data) || isSaveAndExitResponse(data) || isEditingStartedMessage(data) || isSetBaseLineResponse(data)) {
      return true;
    }
        
    // Also handle nested message structures (some messages come wrapped)
    if (typeof data === "object" && data !== null) {
      const obj = data as Record<string, unknown>;
      const messageValue = obj.message;
      
      if (messageValue && typeof messageValue === "object") {
        if (isSaveAndExitResponse(messageValue) || isEditingStartedMessage(messageValue) || isSetBaseLineResponse(messageValue)) {
          return true;
        }
      }
    }
    
    return false;
  }
}

// Lazy-initialised default instance so importing this module does not throw
// when REACT_APP_BINTACT_MAP_URL is unset (e.g. deployments not using BINTACT).
let defaultInstance: AgroInformaticsMapService | null = null;
export const agroInformaticsMapService = new Proxy({} as AgroInformaticsMapService, {
  get(_target, prop) {
    if (!defaultInstance) {
      defaultInstance = new AgroInformaticsMapService();
    }
    return Reflect.get(defaultInstance, prop, defaultInstance);
  },
});

